import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import * as pdfjsLib from 'pdfjs-dist';
import { readFileAsArrayBuffer } from '../utils/pdfExport';

const uid = () => Math.random().toString(36).slice(2, 9);

/**
 * Load a single PDF File, extract metadata, and render a first-page thumbnail.
 */
async function loadPdfItem(file) {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) }).promise;
  const pageCount = pdfDoc.numPages;

  // Render page 1 at a resolution suitable for grid display (~220px wide)
  const page = await pdfDoc.getPage(1);
  const naturalVP = page.getViewport({ scale: 1 });
  const scale = 220 / naturalVP.width;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  const thumbnail = canvas.toDataURL('image/jpeg', 0.82);
  const thumbnailAspect = viewport.height / viewport.width;

  return {
    id: uid(),
    file,
    fileName: file.name,
    pageCount,
    pdfDoc,
    arrayBuffer,
    thumbnail,
    thumbnailAspect,
  };
}

export function usePdfMerge() {
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]);

  // ── File management ────────────────────────────────────────────────────────

  const addFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setIsLoadingFiles(true);
    setLoadErrors([]);
    const errors = [];
    const loaded = [];

    for (const file of files) {
      try {
        const item = await loadPdfItem(file);
        loaded.push(item);
      } catch (err) {
        const isEncrypted =
          err?.name === 'PasswordException' ||
          err?.message?.toLowerCase().includes('password');
        errors.push({
          fileName: file.name,
          message: isEncrypted
            ? 'Password-protected — cannot merge encrypted PDFs.'
            : 'Could not open file. Make sure it is a valid PDF.',
        });
      }
    }

    setItems((prev) => [...prev, ...loaded]);
    setLoadErrors(errors);
    setIsLoadingFiles(false);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item?.pdfDoc) {
        try { item.pdfDoc.destroy(); } catch { /* ignore */ }
      }
      return prev.filter((p) => p.id !== id);
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const reorderItems = useCallback((activeId, overId) => {
    setItems((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === activeId);
      const newIndex = prev.findIndex((p) => p.id === overId);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const reset = useCallback(() => {
    setItems((prev) => {
      prev.forEach((item) => {
        try { item.pdfDoc?.destroy(); } catch { /* ignore */ }
      });
      return [];
    });
    setSelectedIds(new Set());
    setLoadErrors([]);
  }, []);

  // ── Selection ──────────────────────────────────────────────────────────────

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setItems((prev) => {
      setSelectedIds(new Set(prev.map((p) => p.id)));
      return prev;
    });
  }, []);

  const deselectAll = useCallback(() => setSelectedIds(new Set()), []);

  const deleteSelected = useCallback(() => {
    setItems((prev) => {
      const toRemove = prev.filter((p) => selectedIds.has(p.id));
      toRemove.forEach((item) => {
        try { item.pdfDoc?.destroy(); } catch { /* ignore */ }
      });
      return prev.filter((p) => !selectedIds.has(p.id));
    });
    setSelectedIds(new Set());
  }, [selectedIds]);

  // ── Move operations (work on current selection) ────────────────────────────

  const moveUp = useCallback(() => {
    setItems((prev) => {
      const positions = prev
        .map((p, i) => (selectedIds.has(p.id) ? i : -1))
        .filter((i) => i >= 0)
        .sort((a, b) => a - b);
      if (positions.length === 0 || positions[0] === 0) return prev;
      const next = [...prev];
      for (const idx of positions) {
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      }
      return next;
    });
  }, [selectedIds]);

  const moveDown = useCallback(() => {
    setItems((prev) => {
      const positions = prev
        .map((p, i) => (selectedIds.has(p.id) ? i : -1))
        .filter((i) => i >= 0)
        .sort((a, b) => b - a);
      if (positions.length === 0 || positions[0] === prev.length - 1) return prev;
      const next = [...prev];
      for (const idx of positions) {
        if (idx < next.length - 1) {
          [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        }
      }
      return next;
    });
  }, [selectedIds]);

  const moveToFirst = useCallback(() => {
    setItems((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      return [...selected, ...rest];
    });
  }, [selectedIds]);

  const moveToLast = useCallback(() => {
    setItems((prev) => {
      const selected = prev.filter((p) => selectedIds.has(p.id));
      const rest = prev.filter((p) => !selectedIds.has(p.id));
      return [...rest, ...selected];
    });
  }, [selectedIds]);

  const totalPages = items.reduce((sum, item) => sum + item.pageCount, 0);

  return {
    items,
    selectedIds,
    isLoadingFiles,
    loadErrors,
    totalPages,
    addFiles,
    removeItem,
    reorderItems,
    reset,
    toggleSelect,
    selectAll,
    deselectAll,
    deleteSelected,
    moveUp,
    moveDown,
    moveToFirst,
    moveToLast,
  };
}
